import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Card, StatusPill } from '@/components/Primitives';
import { TID } from '@/constants/testIds/voice';
import { Plug, FloppyDisk, ArrowsClockwise } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';

const DidwwSettings = () => {
    const [s, setS] = useState(null);
    const [apiKey, setApiKey] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const { toast } = useToast();

    const load = async () => {
        const r = await api.get('/didww/settings');
        setS(r.data);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e?.preventDefault();
        setSaving(true);
        try {
            const payload = { ...s };
            if (apiKey) payload.api_key = apiKey;
            delete payload.api_key_set;
            delete payload.last_connection_test_at;
            delete payload.last_connection_test_ok;
            delete payload.last_connection_test_message;
            delete payload.updated_at;
            delete payload.id;
            const r = await api.put('/didww/settings', payload);
            setS(r.data);
            setApiKey('');
            toast({ title: 'Uloženo', description: 'Nastavení DIDWW bylo aktualizováno.' });
        } catch (err) {
            toast({ title: 'Chyba', description: err.response?.data?.detail || 'Nepodařilo se uložit', variant: 'destructive' });
        } finally {
            setSaving(false);
        }
    };

    const testConnection = async () => {
        setTesting(true);
        try {
            const r = await api.post('/didww/test-connection');
            toast({
                title: r.data.ok ? 'Spojení OK' : 'Spojení selhalo',
                description: r.data.message,
                variant: r.data.ok ? 'default' : 'destructive',
            });
            await load();
        } catch (err) {
            toast({ title: 'Chyba', description: 'Test selhal', variant: 'destructive' });
        } finally {
            setTesting(false);
        }
    };

    if (loading || !s) return <div>Načítání…</div>;

    return (
        <>
            <PageHeader
                eyebrow="Integrace"
                title="DIDWW Nastavení"
                description="Konfigurace připojení k DIDWW API a parametry vašeho PBX/SBC pro směrování příchozích hovorů."
                actions={
                    <button
                        data-testid={TID.didwwTestBtn}
                        onClick={testConnection}
                        disabled={testing}
                        className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] border hover:bg-[var(--bg-surface-hover)]"
                        style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                    >
                        <Plug size={14} weight="bold" />
                        {testing ? 'Testuji…' : 'Test připojení'}
                    </button>
                }
            />

            <div className="mb-4">
                <StatusPill
                    status={s.last_connection_test_ok ? 'success' : (s.last_connection_test_ok === false ? 'error' : 'neutral')}
                    label={
                        s.last_connection_test_ok ? 'API OK'
                        : s.last_connection_test_ok === false ? 'API CHYBA'
                        : 'NETESTOVÁNO'
                    }
                />
                {s.last_connection_test_message && (
                    <span className="ml-3 text-xs font-mono"
                          style={{ color: 'var(--text-secondary)' }}>
                        {s.last_connection_test_message}
                    </span>
                )}
            </div>

            <form onSubmit={save} className="space-y-6">
                <Card title="API Credentials">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>API Key</label>
                            <input
                                data-testid={TID.didwwApiKey}
                                type="password"
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                placeholder={s.api_key_set ? '••••••••  (uloženo zašifrovaně)' : 'Vložte DIDWW API klíč'}
                                className="w-full mt-2 px-3 py-2 bg-[var(--bg-app)] border text-sm font-mono"
                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                            />
                            <p className="text-[11px] mt-1 font-mono" style={{ color: 'var(--text-disabled)' }}>
                                Klíč je šifrován pomocí Fernet (AES-128) a nikdy se nevrací zpět v API.
                            </p>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>API Base URL</label>
                            <input
                                data-testid={TID.didwwBaseUrl}
                                value={s.api_base_url || ''}
                                onChange={(e) => setS({ ...s, api_base_url: e.target.value })}
                                className="w-full mt-2 px-3 py-2 bg-[var(--bg-app)] border text-sm font-mono"
                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                            />
                        </div>
                    </div>
                </Card>

                <Card title="Routing & PBX">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>Routing režim</label>
                            <select
                                data-testid={TID.didwwRoutingMode}
                                value={s.routing_mode || 'static_sip_uri'}
                                onChange={(e) => setS({ ...s, routing_mode: e.target.value })}
                                className="w-full mt-2 px-3 py-2 bg-[var(--bg-app)] border text-sm"
                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                            >
                                <option value="static_sip_uri">Static SIP URI (PBX musí být veřejně dostupný)</option>
                                <option value="sip_registration">SIP Registration (PBX se registruje k DIDWW)</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>PBX / SBC veřejný host</label>
                            <input
                                value={s.pbx_public_host || ''}
                                onChange={(e) => setS({ ...s, pbx_public_host: e.target.value })}
                                placeholder="sip.example.cz"
                                className="w-full mt-2 px-3 py-2 bg-[var(--bg-app)] border text-sm font-mono"
                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>SIP Port (UDP/TCP)</label>
                            <input
                                type="number"
                                value={s.pbx_sip_port || 5060}
                                onChange={(e) => setS({ ...s, pbx_sip_port: parseInt(e.target.value || '5060', 10) })}
                                className="w-full mt-2 px-3 py-2 bg-[var(--bg-app)] border text-sm font-mono"
                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>TLS Port</label>
                            <input
                                type="number"
                                value={s.pbx_tls_port || 5061}
                                onChange={(e) => setS({ ...s, pbx_tls_port: parseInt(e.target.value || '5061', 10) })}
                                className="w-full mt-2 px-3 py-2 bg-[var(--bg-app)] border text-sm font-mono"
                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>Transport</label>
                            <select
                                value={s.transport || 'udp'}
                                onChange={(e) => setS({ ...s, transport: e.target.value })}
                                className="w-full mt-2 px-3 py-2 bg-[var(--bg-app)] border text-sm"
                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                            >
                                <option value="udp">UDP</option>
                                <option value="tcp">TCP</option>
                                <option value="tls">TLS</option>
                            </select>
                        </div>
                        <div className="flex items-end">
                            <label className="inline-flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={!!s.enable_srtp}
                                    onChange={(e) => setS({ ...s, enable_srtp: e.target.checked })}
                                />
                                Povolit SRTP (šifrované médium)
                            </label>
                        </div>
                    </div>
                </Card>

                <Card title="Poznámky">
                    <textarea
                        value={s.notes || ''}
                        onChange={(e) => setS({ ...s, notes: e.target.value })}
                        rows={3}
                        placeholder="Interní poznámky k integraci…"
                        className="w-full px-3 py-2 bg-[var(--bg-app)] border text-sm"
                        style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                    />
                </Card>

                <div className="flex gap-3 items-center">
                    <button
                        data-testid={TID.didwwSaveBtn}
                        disabled={saving}
                        type="submit"
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-[0.08em]"
                        style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}
                    >
                        <FloppyDisk size={14} weight="bold" />
                        {saving ? 'Ukládám…' : 'Uložit nastavení'}
                    </button>
                    <button
                        type="button"
                        onClick={async () => {
                            try {
                                const r = await api.post('/didww/dids/sync');
                                toast({ title: 'DID Sync', description: `Inserted ${r.data.inserted}, Updated ${r.data.updated}` });
                            } catch (err) {
                                toast({ title: 'Sync selhal', description: err.response?.data?.detail || 'error', variant: 'destructive' });
                            }
                        }}
                        data-testid={TID.didwwSyncDids}
                        className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] border hover:bg-[var(--bg-surface-hover)]"
                        style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                    >
                        <ArrowsClockwise size={14} weight="bold" />
                        Sync DID čísel
                    </button>
                    <button
                        type="button"
                        onClick={async () => {
                            try {
                                const r = await api.post('/didww/trunks/sync');
                                toast({ title: 'Trunk Sync', description: `Inserted ${r.data.inserted}, Updated ${r.data.updated}` });
                            } catch (err) {
                                toast({ title: 'Sync selhal', description: err.response?.data?.detail || 'error', variant: 'destructive' });
                            }
                        }}
                        data-testid={TID.didwwSyncTrunks}
                        className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] border hover:bg-[var(--bg-surface-hover)]"
                        style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                    >
                        <ArrowsClockwise size={14} weight="bold" />
                        Sync Trunků
                    </button>
                    <button
                        type="button"
                        onClick={async () => {
                            try {
                                const r = await api.post('/didww/cdrs/sync');
                                toast({ title: 'CDR Sync', description: `Inserted ${r.data.inserted}` });
                            } catch (err) {
                                toast({ title: 'Sync selhal', description: err.response?.data?.detail || 'error', variant: 'destructive' });
                            }
                        }}
                        data-testid={TID.didwwSyncCdrs}
                        className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] border hover:bg-[var(--bg-surface-hover)]"
                        style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                    >
                        <ArrowsClockwise size={14} weight="bold" />
                        Sync CDR
                    </button>
                </div>
            </form>
        </>
    );
};

export default DidwwSettings;
